import React, { useState } from 'react';
import {
  alpha,
  useTheme,
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon,
  SwapHoriz as SwitchIcon,
} from '@mui/icons-material';

// Shared hooks
import { useAuth } from '../../hooks/auth/useAuth';

/**
 * Organization Switcher Component
 *
 * Allows users to switch between organizations they have access to.
 * Provides visual feedback and handles organization context changes.
 *
 * @param {Object} props
 * @param {string} [props.variant] - Button variant ('contained', 'outlined', 'text')
 * @param {string} [props.size] - Button size ('small', 'medium', 'large')
 * @param {boolean} [props.showIcon] - Whether to show the business icon
 * @param {boolean} [props.showOrgType] - Whether to show organization type
 * @param {boolean} [props.fullWidth] - Whether button should take full width
 * @param {Function} [props.onOrganizationChanged] - Callback when organization changes
 */
const OrganizationSwitcher = ({
  variant = 'outlined',
  size = 'medium',
  showIcon = true,
  showOrgType = true,
  fullWidth = false,
  onOrganizationChanged,
  // Lets a host surface (e.g. the coloured client navbar) restyle the trigger so
  // it matches its own typography instead of looking like a stray form control.
  sx: triggerSx,
}) => {
  const theme = useTheme();
  const {
    organizations,
    currentOrganization,
    hasMultipleOrganizations,
    switchCurrentOrganization,
    isSwitchingOrg,
    refreshPermissions,
  } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState('');
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (hasMultipleOrganizations) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError('');
  };

  const handleOrganizationSwitch = async (organization) => {
    if (organization.id === currentOrganization?.id) {
      handleClose();
      return;
    }

    try {
      setError('');
      const result = await switchCurrentOrganization(organization.id);

      if (result.success) {
        // Refresh permissions for the new organization
        await refreshPermissions();

        // Notify parent component
        if (onOrganizationChanged) {
          onOrganizationChanged(organization);
        }

        handleClose();
      } else {
        setError(result.error || 'Failed to switch organization');
      }
    } catch (err) {
      setError('An error occurred while switching organizations');
      console.error('Organization switch error:', err);
    }
  };

  const getOrganizationIcon = (orgType) => {
    return orgType === 'fleetcam' ? '🏢' : '👥';
  };

  const getOrganizationTypeColor = (orgType) => {
    return orgType === 'fleetcam' ? 'primary' : 'secondary';
  };

  const getOrganizationTypeLabel = (orgType) => {
    return orgType === 'fleetcam' ? 'FleetCam' : 'Client';
  };

  // Don't render if user has no organizations or only one organization
  if (!hasMultipleOrganizations) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showIcon && <BusinessIcon color="action" />}
        <Typography variant="body2" color="text.secondary">
          {currentOrganization?.name || 'No Organization'}
        </Typography>
        {showOrgType && currentOrganization && (
          <Chip
            label={getOrganizationTypeLabel(currentOrganization.type)}
            color={getOrganizationTypeColor(currentOrganization.type)}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        onClick={handleClick}
        disabled={isSwitchingOrg}
        startIcon={showIcon && <BusinessIcon />}
        endIcon={
          isSwitchingOrg ? <CircularProgress size={16} color="inherit" /> : <ArrowDownIcon />
        }
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          // `inherit` so the trigger picks up whatever colour its container uses —
          // white on the client portal's blue bar, normal text elsewhere — in both
          // light and dark mode.
          color: 'inherit',
          ...triggerSx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body2" component="span" noWrap>
            {currentOrganization?.name || 'Select Organization'}
          </Typography>
          {showOrgType && currentOrganization && (
            <Chip
              label={getOrganizationTypeLabel(currentOrganization.type)}
              color={getOrganizationTypeColor(currentOrganization.type)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 300,
            maxWidth: 400,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Switch Organization
          </Typography>
        </Box>
        <Divider />

        {/* Error Alert */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" size="small">
              {error}
            </Alert>
          </Box>
        )}

        {/* Organization List */}
        {organizations.map((organization) => {
          const isSelected = organization.id === currentOrganization?.id;

          return (
            <MenuItem
              key={organization.id}
              onClick={() => handleOrganizationSwitch(organization)}
              disabled={isSwitchingOrg}
              sx={{
                py: 1.5,
                backgroundColor: isSelected ? 'action.selected' : 'transparent',
              }}
            >
              <ListItemIcon>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '1rem',
                    bgcolor: isSelected ? 'primary.main' : alpha(theme.palette.text.primary, 0.12),
                    color: isSelected
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.secondary,
                    fontWeight: 600,
                  }}
                >
                  {(organization.name || '?').charAt(0).toUpperCase()}
                </Avatar>
              </ListItemIcon>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{organization.name}</Typography>
                    {isSelected && <CheckIcon color="primary" fontSize="small" />}
                  </Box>
                }
                secondary={
                  // The type chip is meaningless to a client user — every one of
                  // their organisations is a client organisation — so it only
                  // appears where the caller asks for it.
                  showOrgType || organization.is_default ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {showOrgType && (
                        <Chip
                          label={getOrganizationTypeLabel(organization.type)}
                          color={getOrganizationTypeColor(organization.type)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {organization.is_default && (
                        <Chip label="Default" color="success" size="small" variant="outlined" />
                      )}
                    </Box>
                  ) : null
                }
              />
            </MenuItem>
          );
        })}

        <Divider />

        {/* Footer */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {organizations.length} organization{organizations.length !== 1 ? 's' : ''} available
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
};

export default OrganizationSwitcher;
